import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import cn from 'classnames';

import { Tag } from '@wf/components';

import { type ILpcoResponse } from '../../../License/types/response';

import { type IComparisonFieldProps } from './types';

import { versionComparisonSelector } from '../../../../store/audit/selectors';

import styles from './ComparisonField.module.scss';

function ComparisonField({
  label,
  fieldKey,
  tagKey,
  differenceKey,
  isOldVersion,
  render,
  renderTag,
  className,
  values,
  pathPrefix = '/',
}: IComparisonFieldProps) {
  const { t } = useTranslation();
  const { differenceMap } = useSelector(versionComparisonSelector);

  const path = `${pathPrefix}${fieldKey}`;
  const valueHasChanged = !!differenceMap[path];
  const value = valueHasChanged ? differenceMap[path]?.[differenceKey] : (values[fieldKey] as string | number);

  const formattedValue = useMemo(() => {
    if (value == null || value === '') {
      return '-';
    }

    if (render) {
      return render(value);
    }

    return value;
  }, [render, value]);

  let tag = null;

  if (tagKey) {
    const tagPath = `${pathPrefix}${tagKey}`;

    const tagValue = valueHasChanged
      ? differenceMap[tagPath]?.[differenceKey]
      : (values[tagKey as keyof ILpcoResponse] as string | number);

    const tagColor = valueHasChanged ? (isOldVersion ? 'error' : 'success') : 'typography';

    tag = tagValue ? (
      <Tag size="sm" className={styles.tag} color={tagColor} title={String(tagValue)}>
        {renderTag ? renderTag(tagValue) : (tagValue as string)}
      </Tag>
    ) : null;
  }

  return (
    <div className={cn(styles.wrapper, className)}>
      <div className={styles.label}>{t(label)}</div>
      <div
        className={cn(styles.valueContainer, {
          [styles.removed as string]: valueHasChanged && isOldVersion,
          [styles.added as string]: valueHasChanged && !isOldVersion,
          [styles.withTag as string]: !!tag,
        })}
      >
        <div>{formattedValue as string}</div>
        {tag}
      </div>
    </div>
  );
}

export default ComparisonField;
